import { useEffect, useState, useSyncExternalStore } from 'react'
import * as signalR from '@microsoft/signalr'
import { useAuthStore } from '../../../store/authStore'
import type { NetworkDiagram, NetworkDoc } from '../../../types'
import { newKey } from './netModel'

// 네트워크 도면 실시간 공동 편집 (NetworkHub, /hubs/network).
//  · 확정 상태(confirmed) = 서버가 방 전체에 보낸 변경(Ops)을 받은 순서대로 누적한 것 — 모두 같은 순서로 받으므로 모두 같다.
//  · 화면 = 확정 상태 + 아직 서버가 확인 안 한 내 변경(pending: 보냄, outbox: 모으는 중). 내 변경이 돌아오면 pending 에서 뺀다.
//    같은 항목을 둘이 고치면 서버에 나중에 도착한 쪽이 남고, 두 화면 다 그 값으로 맞춰진다.
//  · 되돌리기는 내가 바꾼 항목만 이전 값으로 되돌린다 (남이 바꾼 다른 항목은 건드리지 않음).
//  · 글자 입력처럼 잦은 변경은 0.25초 모아서 보낸다.

export type Coll = 'segments' | 'nodes' | 'links'
type Item = { key: string }
export type Op = { coll: Coll; key: string; value: Item | null }
type Batch = { id: string; ops: Op[] }
type Change = { coll: Coll; key: string; before: Item | null; after: Item | null }
type Changeset = { at: number; items: Map<string, Change> }

export type Viewer = { connectionId: string; userId: string | null; name: string; color: number; selected: string[] }
export type NodeMove = { key: string; x: number; y: number }
export type RemoteMove = NodeMove & { connectionId: string }
export type SyncStatus = 'connecting' | 'live' | 'saving' | 'reconnecting' | 'error'

const COLLS: Coll[] = ['segments', 'nodes', 'links']
const FLUSH_MS = 250
const COALESCE_MS = 800
const HISTORY_LIMIT = 100

export const VIEWER_COLORS = ['#e11d48', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d']

const id = (o: Op | Change) => `${o.coll}:${o.key}`
const emptyDoc = (): NetworkDoc => ({ segments: [], nodes: [], links: [] })

export function applyOps(doc: NetworkDoc, ops: Op[]): NetworkDoc {
  if (ops.length === 0) return doc
  const out = { segments: [...doc.segments], nodes: [...doc.nodes], links: [...doc.links] } as Record<Coll, Item[]>
  for (const op of ops) {
    const list = out[op.coll]
    if (!list) continue
    const at = list.findIndex(x => x.key === op.key)
    if (op.value == null) { if (at >= 0) list.splice(at, 1) }
    else if (at >= 0) list[at] = op.value
    else list.push(op.value)
  }
  // 지워진 노드를 잇는 연결은 그리지 않는다 (서버가 곧 삭제를 보낸다)
  const nodes = new Set(out.nodes.map(n => n.key))
  return { ...(out as unknown as NetworkDoc), links: (out.links as NetworkDoc['links']).filter(l => nodes.has(l.from) && nodes.has(l.to)) }
}

function diff(a: NetworkDoc, b: NetworkDoc): Change[] {
  const changes: Change[] = []
  for (const coll of COLLS) {
    const before = new Map((a[coll] as Item[]).map(x => [x.key, x]))
    const after = new Map((b[coll] as Item[]).map(x => [x.key, x]))
    for (const [key, v] of after) {
      const old = before.get(key)
      if (!old || (old !== v && JSON.stringify(old) !== JSON.stringify(v))) changes.push({ coll, key, before: old ?? null, after: v })
    }
    for (const [key, old] of before) if (!after.has(key)) changes.push({ coll, key, before: old, after: null })
  }
  return changes
}

type Snapshot = {
  doc: NetworkDoc
  status: SyncStatus
  error: string | null
  viewers: Viewer[]
  me: string | null
  moves: RemoteMove[]
  canUndo: boolean
  canRedo: boolean
  ready: boolean
}

class NetSync {
  private conn: signalR.HubConnection | null = null
  private joining = false
  private buffered: { batchId: string; ops: Op[]; revision: number }[] = []
  private confirmed = emptyDoc()
  private rev = 0
  private pending: Batch[] = []
  private outbox = new Map<string, Op>()
  private past: Changeset[] = []
  private future: Changeset[] = []
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private moveTimer: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<() => void>()
  private snap: Snapshot = { doc: emptyDoc(), status: 'connecting', error: null, viewers: [], me: null, moves: [], canUndo: false, canRedo: false, ready: false }
  private moves = new Map<string, { items: NodeMove[]; at: number }>() // 접속자별 끄는 중인 노드들
  private disposed = false
  private serverId: string
  private onFirstJoin: ((doc: NetworkDoc) => void) | null

  constructor(serverId: string, onFirstJoin: (doc: NetworkDoc) => void) {
    this.serverId = serverId
    this.onFirstJoin = onFirstJoin
  }

  private get connected() { return this.conn?.state === signalR.HubConnectionState.Connected }

  /** 연결을 연다 (StrictMode 처럼 열고 닫고 다시 열어도 된다) */
  start() {
    this.disposed = false
    const conn = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/network', { accessTokenFactory: () => useAuthStore.getState().accessToken ?? '' })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()
    conn.on('NetworkOps', (batchId: string, ops: Op[], _by: string, revision: number) => this.receive(batchId, ops, revision))
    conn.on('NetworkPresence', (viewers: Viewer[]) => this.set({ viewers }))
    conn.on('NetworkMoving', (connectionId: string, items: NodeMove[]) => {
      this.moves.set(connectionId, { items, at: Date.now() })
      this.publishMoves()
    })
    conn.onreconnecting(() => this.set({ status: 'reconnecting' }))
    conn.onreconnected(() => this.join())
    conn.onclose(() => { if (!this.disposed && this.conn === conn) this.set({ status: 'error', error: '실시간 연결이 끊겼습니다. 새로고침하세요.' }) })
    this.conn = conn
    this.set({ status: 'connecting', error: null })
    conn.start().then(() => { if (this.conn === conn) this.join() })
      .catch(err => { if (this.conn === conn) this.set({ status: 'error', error: messageOf(err, '실시간 연결에 실패했습니다.') }) })
  }

  dispose() {
    this.disposed = true
    this.flush()
    const conn = this.conn
    this.conn = null
    // 남은 변경이 서버에 닿을 시간을 조금 준 뒤 닫는다
    setTimeout(() => conn?.stop(), this.pending.length ? 1500 : 0)
  }

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn) } }
  getSnapshot = () => this.snap

  // ── 연결·수신 ──

  private async join() {
    if (!this.conn || this.joining) return
    this.joining = true
    try {
      const r = await this.conn.invoke<{ connectionId: string; diagram: NetworkDiagram; viewers: Viewer[] }>('Join', this.serverId)
      this.confirmed = { segments: r.diagram.segments, nodes: r.diagram.nodes, links: r.diagram.links }
      this.rev = r.diagram.revision
      this.joining = false
      // 들어오는 동안 도착한 변경 — 원본에 이미 든 것(revision 이하)은 건너뛴다
      const buffered = this.buffered
      this.buffered = []
      for (const b of buffered) this.receive(b.batchId, b.ops, b.revision)
      const first = !this.snap.ready
      this.set({ me: r.connectionId, viewers: r.viewers, ready: true, error: null })
      // 재접속이면 확인 못 받은 변경을 다시 보낸다 (같은 batchId — 돌아오면 지운다. 항목을 통째로 넣는 변경이라 두 번 가도 같다)
      for (const b of this.pending) this.send(b)
      this.flush()
      if (first && this.onFirstJoin) { const fn = this.onFirstJoin; this.onFirstJoin = null; fn(this.view()) }
    } catch (err) {
      this.joining = false
      this.set({ status: 'error', error: messageOf(err, '도면에 들어가지 못했습니다.') })
    }
  }

  private receive(batchId: string, ops: Op[], revision: number) {
    if (this.joining) { this.buffered.push({ batchId, ops, revision }); return }
    if (revision <= this.rev) { this.pending = this.pending.filter(b => b.id !== batchId); this.publish(); return }
    if (revision !== this.rev + 1) {
      // 놓친 변경이 있다 — 원본을 다시 받는다
      this.join()
      return
    }
    this.rev = revision
    this.confirmed = applyOps(this.confirmed, ops)
    this.pending = this.pending.filter(b => b.id !== batchId)
    // 남이 옮긴 노드는 끄는 중 표시를 지운다
    const keys = new Set(ops.map(op => op.key))
    for (const [c, m] of this.moves) if (m.items.some(i => keys.has(i.key))) this.moves.delete(c)
    this.publish()
  }

  // ── 내 변경 ──

  /** 화면 상태를 바꾼다 — 바뀐 항목만 서버로 보낸다 */
  update(fn: (d: NetworkDoc) => NetworkDoc) {
    const view = this.view()
    const changes = diff(view, fn(view))
    if (changes.length === 0) return
    this.record(changes)
    this.queue(changes.map(c => ({ coll: c.coll, key: c.key, value: c.after })))
  }

  undo() { this.travel(this.past, this.future, 'before') }
  redo() { this.travel(this.future, this.past, 'after') }

  private travel(from: Changeset[], to: Changeset[], side: 'before' | 'after') {
    const cs = from.pop()
    if (!cs) return
    to.push(cs)
    this.queue([...cs.items.values()].map(c => ({ coll: c.coll, key: c.key, value: c[side] })))
  }

  private record(changes: Change[]) {
    const now = Date.now()
    const last = this.past[this.past.length - 1]
    if (last && now - last.at < COALESCE_MS) {
      for (const c of changes) {
        const prev = last.items.get(id(c))
        last.items.set(id(c), prev ? { ...c, before: prev.before } : c)
      }
      last.at = now
    } else {
      this.past.push({ at: now, items: new Map(changes.map(c => [id(c), c])) })
      if (this.past.length > HISTORY_LIMIT) this.past.shift()
    }
    this.future = []
  }

  private queue(ops: Op[]) {
    for (const op of ops) this.outbox.set(id(op), op)
    if (this.flushTimer) clearTimeout(this.flushTimer)
    this.flushTimer = setTimeout(() => this.flush(), FLUSH_MS)
    this.publish()
  }

  private flush() {
    if (this.flushTimer) { clearTimeout(this.flushTimer); this.flushTimer = null }
    if (this.outbox.size === 0 || !this.connected || !this.snap.ready || this.joining) return
    const batch = { id: newKey(), ops: [...this.outbox.values()] }
    this.outbox.clear()
    this.pending.push(batch)
    this.send(batch)
    this.publish()
  }

  private send(batch: Batch) {
    this.conn?.invoke('Apply', this.serverId, batch.id, batch.ops).catch(err => {
      // 거절된 변경은 버리고 화면을 서버 상태로 되돌린다
      this.pending = this.pending.filter(b => b.id !== batch.id)
      this.set({ error: messageOf(err, '변경을 저장하지 못했습니다.') })
      this.publish()
    })
  }

  // ── 표시 전용 ──

  select(nodeKeys: string[]) {
    if (this.connected) this.conn!.invoke('Select', this.serverId, nodeKeys).catch(() => {})
  }

  private lastMove = 0
  moving(items: NodeMove[]) {
    const now = Date.now()
    if (!items.length || now - this.lastMove < 60 || !this.connected) return
    this.lastMove = now
    this.conn!.invoke('Moving', this.serverId, items).catch(() => {})
  }

  private flatMoves(): RemoteMove[] {
    return [...this.moves].flatMap(([connectionId, m]) => m.items.map(i => ({ ...i, connectionId })))
  }

  private publishMoves() {
    // 3초 넘게 소식이 없는 끄는 중 표시는 지운다
    if (this.moveTimer) clearTimeout(this.moveTimer)
    this.moveTimer = setTimeout(() => {
      const cutoff = Date.now() - 3000
      for (const [c, m] of this.moves) if (m.at < cutoff) this.moves.delete(c)
      this.set({ moves: this.flatMoves() })
    }, 3100)
    this.set({ moves: this.flatMoves() })
  }

  // ── 상태 ──

  private view(): NetworkDoc {
    return applyOps(applyOps(this.confirmed, this.pending.flatMap(b => b.ops)), [...this.outbox.values()])
  }

  private publish() {
    const busy = this.pending.length > 0 || this.outbox.size > 0
    const status: SyncStatus = this.snap.status === 'error' || this.snap.status === 'reconnecting' ? this.snap.status
      : !this.snap.ready ? 'connecting' : busy ? 'saving' : 'live'
    this.set({ doc: this.view(), status, moves: this.flatMoves(), canUndo: this.past.length > 0, canRedo: this.future.length > 0 })
  }

  private set(patch: Partial<Snapshot>) {
    this.snap = { ...this.snap, ...patch }
    if (patch.ready || patch.me) {
      // 들어온 직후·재접속 뒤에는 상태를 다시 계산
      const busy = this.pending.length > 0 || this.outbox.size > 0
      this.snap = { ...this.snap, doc: this.view(), status: busy ? 'saving' : 'live' }
    }
    for (const fn of this.listeners) fn()
  }
}

function messageOf(err: unknown, fallback: string) {
  const m = err instanceof Error ? err.message : ''
  // HubException 메시지는 "... HubException: 내용" 꼴
  const hub = /HubException: (.+)$/.exec(m)
  return hub ? hub[1] : fallback
}

/** 서버 하나의 도면 공동 편집 연결. onFirstJoin = 처음 들어온 직후 한 번 (Edge 노드 만들기 등) */
export function useNetworkSync(serverId: string, onFirstJoin: (doc: NetworkDoc, sync: NetSyncApi) => void) {
  const [sync] = useState(() => {
    const s: NetSync = new NetSync(serverId, doc => onFirstJoin(doc, api(s)))
    return s
  })
  useEffect(() => {
    sync.start()
    return () => sync.dispose()
  }, [sync])
  const snap = useSyncExternalStore(sync.subscribe, sync.getSnapshot)
  return { ...snap, ...api(sync) }
}

export type NetSyncApi = {
  setDoc: (fn: (d: NetworkDoc) => NetworkDoc) => void
  undo: () => void
  redo: () => void
  select: (nodeKeys: string[]) => void
  moving: (items: NodeMove[]) => void
}

const api = (s: NetSync): NetSyncApi => ({
  setDoc: fn => s.update(fn),
  undo: () => s.undo(),
  redo: () => s.redo(),
  select: k => s.select(k),
  moving: items => s.moving(items),
})
